import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsHome.css';

function formatCount(value) {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value || 0).toLocaleString('en-US');
}

function formatUsdFromCents(value) {
  if (value === null || value === undefined || value === '') return '—';
  return (Number(value || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return '—';
  return `${Number(value || 0).toFixed(1)}%`;
}

function formatPercentWhole(value) {
  if (value === null || value === undefined || value === '') return '—';
  return `${Math.round(Number(value || 0) * 100)}%`;
}

function maskEin(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length < 4) return 'EIN —';
  return `EIN ••••${digits.slice(-4)}`;
}

function MetricCard({ title, value, caption }) {
  return (
    <article className="ops-card">
      <div className="ops-card-title">{title}</div>
      <div className="ops-card-value">{value}</div>
      <div className="ops-card-caption">{caption}</div>
    </article>
  );
}

const DEFAULT_FILTERS = {
  market: 'all',
  state: 'all',
  window: '30d',
  brandId: '',
  ein: '',
  storeId: '',
  causeId: '',
  attributionView: 'account',
  customStart: '',
  customEnd: '',
};

export default function OpsPerformance() {
  const navigate = useNavigate();
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  async function loadPerformance(activeFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('market', activeFilters.market || 'all');
      params.set('state', activeFilters.state || 'all');
      params.set('window', activeFilters.window || '30d');
      if (activeFilters.brandId) params.set('brandId', activeFilters.brandId);
      if (activeFilters.ein) params.set('ein', activeFilters.ein);
      if (activeFilters.storeId) params.set('storeId', activeFilters.storeId);
      if (activeFilters.causeId) params.set('causeId', activeFilters.causeId);
      if (activeFilters.attributionView) params.set('attributionView', activeFilters.attributionView);
      if (activeFilters.window === 'custom') {
        if (activeFilters.customStart) params.set('start', activeFilters.customStart);
        if (activeFilters.customEnd) params.set('end', activeFilters.customEnd);
      }

      const resp = await apiFetch(`/ops/program-health/performance?${params.toString()}`);
      setData(resp || null);
    } catch (err) {
      const status = err?.status;
      if (status === 401 || status === 403) {
        clearAuthToken();
        navigate('/login', { replace: true });
        return;
      }
      setData(null);
      setError(err?.data?.error || err?.message || 'Failed to load performance metrics');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPerformance(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (!data?.filters?.selected) return;
    const selected = data.filters.selected;
    setDraftFilters((prev) => ({
      ...prev,
      market: selected.market || prev.market,
      state: selected.state || prev.state,
      brandId: selected.brandId || '',
      ein: selected.ein || '',
      storeId: selected.storeId || '',
      causeId: selected.causeId || '',
      attributionView: selected.attributionView || prev.attributionView,
    }));
  }, [data]);

  const metrics = data?.metrics || {};
  const product = metrics.product || {};
  const trust = metrics.trust || {};
  const operation = metrics.operation || {};
  const behavior = metrics.behavior || {};
  const drilldowns = metrics.drilldowns || {};

  const productCards = [
    {
      title: 'Consumers Enrolled',
      value: formatCount(product.consumersEnrolled),
      caption: `${formatCount(product.consumerRegistrationInProgress)} in-progress • TMAU ${formatCount(product.mau)} • TWAU ${formatCount(product.wau)} • TDAU ${formatCount(product.dau)}`,
    },
    {
      title: 'Businesses Enrolled',
      value: formatCount(product.businessesEnrolled?.accounts),
      caption: `${formatCount(product.businessesEnrolled?.eins)} EINs • ${formatCount(product.businessesEnrolled?.stores)} stores`,
    },
    {
      title: 'Causes Enrolled',
      value: formatCount(product.causesEnrolled?.live),
      caption: `${formatCount(product.causesEnrolled?.payoutReady)} payout-ready`,
    },
    {
      title: 'TMAU Growth',
      value: formatPercent(product.mauGrowthPercent),
      caption: `Window: ${data?.window?.label || '—'}`,
    },
    {
      title: 'Cost To Serve: Platform',
      value: formatUsdFromCents(product.infraCostCents),
      caption: 'Platform-wide operating cost',
    },
    {
      title: 'Cost To Serve: Consumer',
      value: formatUsdFromCents(product.costPerActiveUserCents),
      caption: 'Per active consumer (beta placeholder)',
    },
    {
      title: 'Cost To Serve: Business',
      value: '—',
      caption: 'Not fully tracked in beta',
    },
    {
      title: 'Cost To Serve: NFP',
      value: '—',
      caption: 'Not fully tracked in beta',
    },
  ];

  const trustCards = [
    {
      title: 'Full Match Rate',
      value: formatPercent(trust.matchRatePercent),
      caption: `${formatCount(trust.fullyMatchedTransactions)} of ${formatCount(trust.importedTransactions)} fully matched`,
    },
    {
      title: 'Refund Rate',
      value: formatPercent(trust.refundRatePercent),
      caption: `${formatCount(trust.refundCount)} refunds`,
    },
    {
      title: 'Refund Impact',
      value: formatUsdFromCents(trust.refundAmountCents),
      caption: `Contribution reversal ${formatUsdFromCents(trust.refundContributionImpactCents)}`,
    },
    {
      title: 'On-Time Payout Accuracy',
      value: formatPercent(trust.onTimePayoutAccuracyPercent),
      caption: 'Not tracked in beta',
    },
  ];

  const operationCards = [
    {
      title: 'Fee Revenue',
      value: formatUsdFromCents(operation.feeRevenueCents),
      caption: `Collectable fees • Credits ${formatUsdFromCents(operation.feeCreditsCents)} • Overall ${formatUsdFromCents(operation.feeOverallCents)} • Net ${formatUsdFromCents(operation.netRevenueCents)}`,
    },
    {
      title: 'Cost To Enter Market',
      value: formatUsdFromCents(operation.launchCostCents),
      caption: 'Not tracked in beta',
    },
    {
      title: 'Time To Launch',
      value: operation.timeToLaunchDays == null ? '—' : `${formatCount(operation.timeToLaunchDays)} days`,
      caption: 'Not tracked in beta',
    },
    {
      title: 'Monthly Ops Cost',
      value: formatUsdFromCents(operation.monthlyOpsCostCents),
      caption: 'Not tracked in beta',
    },
  ];

  const behaviorCards = [
    {
      title: 'Transactions / User / Month',
      value: behavior.transactionsPerUser == null ? '—' : Number(behavior.transactionsPerUser || 0).toFixed(2),
      caption: 'Eligible partner transactions per active user',
    },
    {
      title: 'Avg Transaction Amount',
      value: formatUsdFromCents(behavior.averageTransactionAmountCents),
      caption: `Wallet deduction / tx: ${formatUsdFromCents(behavior.averageWalletDebitPerTransactionCents)}`,
    },
    {
      title: 'Avg Monthly Eligible Spend / User',
      value: formatUsdFromCents(behavior.averageEligibleSpendPerUserCents),
      caption: `Wallet deduction / user: ${formatUsdFromCents(behavior.averageWalletDebitPerUserCents)}`,
    },
    {
      title: 'New-To-Brand Rate',
      value: formatPercent(behavior.newToBrandRatePercent),
      caption: 'Not tracked in beta',
    },
  ];

  function openEinPerformer(row) {
    if (!row?.brandId) return;
    const params = new URLSearchParams();
    params.set('einId', row.einId || row.brandId);
    if (row.topDbaId) params.set('storeId', row.topDbaId);
    navigate(`/ops/businesses/${row.brandId}?${params.toString()}`);
  }

  function openAccountView(row) {
    if (!row?.brandId) return;
    navigate(`/ops/businesses/${row.brandId}`);
  }

  return (
    <OpsLayout
      title="Performance"
      subtitle="Beta performance metrics across Product, Trust, Operation, and behavior change"
    >
      <div className="ops-nfp-content">
        <section className="ops-nfp-panel" style={{ marginTop: 0 }}>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Market</span>
              <select
                value={draftFilters.market}
                onChange={(e) => setDraftFilters((prev) => ({ ...prev, market: e.target.value }))}
              >
                {(Array.isArray(data?.filters?.markets) ? data.filters.markets : [{ key: 'all', label: 'All Markets' }]).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>State</span>
              <select
                value={draftFilters.state}
                onChange={(e) => setDraftFilters((prev) => ({ ...prev, state: e.target.value, market: 'all' }))}
              >
                {(Array.isArray(data?.filters?.states) ? data.filters.states : [{ key: 'all', label: 'All States' }]).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Date Range</span>
              <select
                value={draftFilters.window}
                onChange={(e) => setDraftFilters((prev) => ({ ...prev, window: e.target.value }))}
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="custom">Custom</option>
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Brand</span>
              <select
                value={draftFilters.brandId}
                onChange={(e) => setDraftFilters((prev) => ({ ...prev, brandId: e.target.value }))}
              >
                <option value="">All Brands</option>
                {(Array.isArray(data?.filters?.brands) ? data.filters.brands : []).map((brand) => (
                  <option key={brand.id} value={brand.id}>{brand.name}{brand.einTaxId ? ` (${brand.einTaxId})` : ''}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>EIN</span>
              <input
                value={draftFilters.ein}
                onChange={(e) => setDraftFilters((prev) => ({ ...prev, ein: e.target.value.trim() }))}
                placeholder="Exact EIN"
              />
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Store</span>
              <input
                value={draftFilters.storeId}
                onChange={(e) => setDraftFilters((prev) => ({ ...prev, storeId: e.target.value.trim() }))}
                placeholder="Store ID or placeId"
              />
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Cause</span>
              <select
                value={draftFilters.causeId}
                onChange={(e) => setDraftFilters((prev) => ({ ...prev, causeId: e.target.value }))}
              >
                <option value="">All Causes</option>
                {(Array.isArray(data?.filters?.causes) ? data.filters.causes : []).map((cause) => (
                  <option key={cause.id} value={cause.id}>{cause.name}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Attribution View</span>
              <select
                value={draftFilters.attributionView}
                onChange={(e) => setDraftFilters((prev) => ({ ...prev, attributionView: e.target.value }))}
              >
                <option value="account">Account-level</option>
                <option value="ein">EIN-level</option>
              </select>
            </label>

            {draftFilters.window === 'custom' ? (
              <>
                <label className="ops-nfp-field">
                  <span style={{ fontWeight: 600 }}>Start</span>
                  <input
                    type="date"
                    value={draftFilters.customStart}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, customStart: e.target.value }))}
                  />
                </label>
                <label className="ops-nfp-field">
                  <span style={{ fontWeight: 600 }}>End</span>
                  <input
                    type="date"
                    value={draftFilters.customEnd}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, customEnd: e.target.value }))}
                  />
                </label>
              </>
            ) : null}
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
            <button type="button" className="ops-btn primary" onClick={() => setFilters({ ...draftFilters })}>
              Apply Filters
            </button>
            <button
              type="button"
              className="ops-btn"
              onClick={() => {
                setDraftFilters(DEFAULT_FILTERS);
                setFilters(DEFAULT_FILTERS);
              }}
            >
              Reset
            </button>
            <span className="ops-window-label">{data?.window?.label || '—'}</span>
          </div>

          {(Array.isArray(data?.filters?.notes) ? data.filters.notes : []).map((note) => (
            <div key={note} className="ops-message" style={{ marginTop: 10 }}>{note}</div>
          ))}
        </section>

        {loading ? <div className="ops-message">Loading performance metrics…</div> : null}
        {!loading && error ? <div className="ops-message error">{error}</div> : null}

        {!loading && !error && data ? (
          <>
            <section className="ops-panel">
              <h2>Product</h2>
              <div className="ops-cards">
                {productCards.map((card) => (
                  <MetricCard key={card.title} {...card} />
                ))}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Trust / Quality</h2>
              <div className="ops-cards">
                {trustCards.map((card) => (
                  <MetricCard key={card.title} {...card} />
                ))}
              </div>
              <div style={{ marginTop: 12 }}>
                <h3 style={{ margin: '0 0 8px 0' }}>Top Unmatched Reasons</h3>
                <div className="ops-queue-table-wrap">
                  <table className="ops-queue-table">
                    <thead>
                      <tr>
                        <th>Reason</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(trust.unmatchedTopReasons || []).length === 0 ? (
                        <tr><td colSpan={2}>No unmatched reasons in this window.</td></tr>
                      ) : (trust.unmatchedTopReasons || []).map((row) => (
                        <tr key={row.reason}>
                          <td>{row.reason || 'unknown'}</td>
                          <td>{formatCount(row.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section className="ops-panel">
              <h2>Operation</h2>
              <div className="ops-cards">
                {operationCards.map((card) => (
                  <MetricCard key={card.title} {...card} />
                ))}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Performance</h2>
              <div className="ops-cards">
                {behaviorCards.map((card) => (
                  <MetricCard key={card.title} {...card} />
                ))}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Top Performers</h2>
              <div style={{ display: 'grid', gap: 16, marginTop: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
                <div>
                  <h3 style={{ margin: '0 0 8px 0' }}>Top Brands</h3>
                  <div className="ops-queue-table-wrap">
                    <table className="ops-queue-table">
                      <thead>
                        <tr>
                          <th>Top Performer</th>
                          <th>Stores</th>
                          <th>Eligible Tx</th>
                          <th>Overall Fees</th>
                          <th>Fee Credits</th>
                          <th>Collectable Fees</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(drilldowns.topBrands || []).length === 0 ? (
                          <tr><td colSpan={6}>No brand data in this window.</td></tr>
                        ) : (drilldowns.topBrands || []).map((row) => (
                          <tr key={row.brandId || row.name}>
                            <td>
                              <div style={{ display: 'grid', gap: 6 }}>
                                <button
                                  type="button"
                                  onClick={() => openEinPerformer(row)}
                                  style={{
                                    border: 'none',
                                    background: 'none',
                                    padding: 0,
                                    margin: 0,
                                    textAlign: 'left',
                                    color: 'var(--ops-ink, #163d4a)',
                                    cursor: row.brandId ? 'pointer' : 'default',
                                    fontWeight: 700,
                                    textDecoration: row.brandId ? 'underline' : 'none',
                                  }}
                                >
                                  {row.topDbaDisplayName || row.name}
                                </button>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--ops-muted, #5d7780)' }}>
                                  <span>{maskEin(row.einTaxId)}</span>
                                  {row.dbaCount > 1 ? <span>{`+${row.dbaCount - 1} DBAs`}</span> : null}
                                  {row.topDbaShare != null ? (
                                    <span>{`Driven by ${formatPercentWhole(row.topDbaShare)}`}</span>
                                  ) : null}
                                  {row.accountDisplayName ? <span>{row.accountDisplayName}</span> : null}
                                  {row.nameMappingIncomplete ? (
                                    <span style={{ color: '#9a4f2d', fontWeight: 600 }}>Name mapping incomplete</span>
                                  ) : null}
                                </div>
                                {row.topDbaDisplayName === 'Mixed' && row.secondDbaDisplayName ? (
                                  <div style={{ fontSize: 12, color: 'var(--ops-muted, #5d7780)' }}>
                                    {`Top DBA: ${row.topDbaPrimaryDisplayName} (${formatPercentWhole(row.topDbaShare)}) • ${row.secondDbaDisplayName} (${formatPercentWhole(row.secondDbaShare)})`}
                                  </div>
                                ) : null}
                                <div>
                                  <button
                                    type="button"
                                    onClick={() => openAccountView(row)}
                                    style={{
                                      border: '1px solid rgba(22, 61, 74, 0.2)',
                                      background: '#fff',
                                      color: 'var(--ops-ink, #163d4a)',
                                      borderRadius: 999,
                                      padding: '4px 10px',
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: row.brandId ? 'pointer' : 'default',
                                    }}
                                  >
                                    View Account
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td>{formatCount(row.storeCount)}</td>
                            <td>{formatCount(row.eligibleTransactions)}</td>
                            <td>{formatUsdFromCents(row.feeOverallCents ?? row.feeCents)}</td>
                            <td>{formatUsdFromCents(row.feeCreditCents)}</td>
                            <td>{formatUsdFromCents(row.collectableFeeCents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 style={{ margin: '0 0 8px 0' }}>Market Leaderboard</h3>
                  <div className="ops-queue-table-wrap">
                    <table className="ops-queue-table">
                      <thead>
                        <tr>
                          <th>Market</th>
                          <th>Accounts</th>
                          <th>EINs</th>
                          <th>Stores</th>
                          <th>TMAU</th>
                          <th>Match Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(drilldowns.marketLeaderboard || []).length === 0 ? (
                          <tr><td colSpan={6}>No market data in this window.</td></tr>
                        ) : (drilldowns.marketLeaderboard || []).map((row) => (
                          <tr key={row.marketKey}>
                            <td>{row.marketKey === 'all' ? 'All Markets' : row.marketKey}</td>
                            <td>{formatCount(row.accountCount)}</td>
                            <td>{formatCount(row.einCount)}</td>
                            <td>{formatCount(row.storeCount)}</td>
                            <td>{formatCount(row.mau)}</td>
                            <td>{formatPercent(row.matchRatePercent)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </OpsLayout>
  );
}
